import { cloneDeep, isArray, isNil, omitBy, toUpper } from "lodash";
import store from "@plugins/store";
import appConfigs from "@configs/app";
import RandomUtils from "@utils/random.utils";
import { redirect } from "@plugins/router";
import { cookieManager } from "@utils/cookie.manager";
import { ERROR_CODE } from "@plugins/common/constant/error.constant";
import i18n from "@plugins/i18n";
import axios from "axios";
import User2FAApi from "@apis/user.2fa.api";
import Auth from "@plugins/otp";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";

/**
 * Config implementations
 */

function base64Encode(str) {
  // source: https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
  // first we use encodeURIComponent to get percent-encoded UTF-8,
  // then we convert the percent encodings into raw bytes which
  // can be fed into btoa.
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
      return String.fromCharCode("0x" + p1);
    }),
  );
}

const requestTransformer = (data, headers) => {
  if (headers["Request-Method"] === "POST" && headers["Content-Type"] === "application/json") {
    if (typeof data !== "undefined" && typeof data !== "string") {
      data = JSON.stringify(data);
    }
  }

  if (headers["X-With-Richtext"]) {
    if (headers["Content-Type"] === "application/json") {
      data = base64Encode(data);
    } else if (isArray(data)) {
      data = encodeArray(data);
    } else if (data instanceof FormData) {
      data = encodeFormData(data);
    } else if (typeof data === "object") {
      data = encodeObject(data);
    }
  }

  return data;
};

function encodeArray(data) {
  const clonedData = cloneDeep(data);
  const encodedData = [];
  for (let i = 0, len = clonedData.length; i < len; i++) {
    if (clonedData[i].value) {
      encodedData.push({
        name: clonedData[i].name,
        value: base64Encode(clonedData[i].value),
      });
    }
  }
  const URLparams = new URLSearchParams(Object.entries(encodedData));
  return URLparams.toString();
}

function encodeFormData(data) {
  const encodedData = {};
  for (const entry of data) {
    encodedData[entry[0]] = base64Encode(entry[1]);
  }
  const URLparams = new URLSearchParams(Object.entries(encodedData));
  return URLparams.toString();
}

function encodeObject(data) {
  const clonedData = cloneDeep(data);
  const encodedData = {};
  for (const prop in clonedData) {
    if (Object.prototype.hasOwnProperty.call(clonedData, prop) && clonedData[prop]) {
      encodedData[prop] = base64Encode(clonedData[prop]);
    }
  }
  const URLparams = new URLSearchParams(Object.entries(encodedData));
  return URLparams.toString();
}

const requestInterceptor = (config) => {
  const method = toUpper(config.method);
  if (method === "POST") {
    config.headers["Request-Identifier"] = RandomUtils.randomUuid();
    if (!config.headers["Content-Type"]) config.headers["Content-Type"] = "application/json";
  }
  if (method === "GET" && !(config.params instanceof URLSearchParams)) {
    const filteredParams = omitBy(config.params, isNil);
    config.params = new URLSearchParams(filteredParams);
  }
  config.headers["Request-Method"] = method;
  config.headers["X-CSRF-TOKEN"] = cookieManager.getCookie("XSRF-TOKEN");
  config.requestStartTime = Date.now();
  return config;
};

const isResponseInvalid = (response) => {
  if (response && response.headers["INVALID_SESSION".toLocaleLowerCase()] == "true") {
    redirect(window.location.href);
    return true;
  }

  if (response && response.headers["CSRF_UNAUTHORIZED_EXCEPTION".toLocaleLowerCase()] == "true") {
    redirect(window.location.href);
    return true;
  }

  return false;
};
const getResponseTime = (response) => {
  const requestStartTime = response.config.requestStartTime;
  const requestEndTime = Date.now();
  return !isNaN(requestStartTime) && requestStartTime > 0
    ? requestEndTime - requestStartTime
    : null;
};
const getQueryMap = (response) => {
  const queryParams = new URLSearchParams(response.request.responseURL.split("?")[1]);
  const queryMap = {};
  queryParams.forEach((value, key) => {
    queryMap[key] = value;
  });
  return queryMap;
};
const isResponseMissing2FA = (response) => {
  return (
    response &&
    response.status === 401 &&
    response.headers["MISSING_2FA_EXCEPTION".toLocaleLowerCase()] === "true"
  );
};
let setServerTimeTimeout = null;

const responseSuccessInterceptor = (response) => {
  if (isResponseInvalid(response)) {
    const sessionManager = window._SLS.sessionManager;
    if (sessionManager) {
      sessionManager.expireSession();
    }

    return;
  }
  const url = response.config.url;
  const queryMap = getQueryMap(response);
  const responseTime = getResponseTime(response);
  userActivityTrackEvent.api({
    httpStatus: 200,
    url: url,
    queryMap: queryMap,
    responseTime: responseTime,
  });
  logger.debug("%s, takes %s secs", url, responseTime / 1000);

  if (response.headers && response.headers.servertime) {
    //if dun have serverTime set at least once 1st
    if (!store.getters["app/serverTime"]) {
      store.dispatch("app/setServerTime", response.headers.servertime);
    } else {
      if (setServerTimeTimeout) {
        clearTimeout(setServerTimeTimeout);
      }
      setServerTimeTimeout = setTimeout(() => {
        store.dispatch("app/setServerTime", response.headers.servertime);
      }, 500);
    }
  }

  return response.data;
};

/**
 * Read SLS GlobalControllerExceptionHandler.java and GlobalAccessDeniedHandler.java
 * @param {*} error
 */
const responseFailInterceptor = (error, httpClient) => {
  const url = error.config?.url;
  const queryMap = getQueryMap(error);
  const responseTime = getResponseTime(error);
  userActivityTrackEvent.api({
    httpStatus: error?.response?.status ?? null,
    url: url,
    queryMap: queryMap,
    responseTime: responseTime,
  });

  if (isResponseMissing2FA(error.response)) {
    logger.error("Session required 2FA");

    return new Promise((resolve) => {
      Auth.$auth.showOTPVerificationModal({
        title: i18n.t("user_2fa_modal_title"),
        content: i18n.t("email_verification_code_OTP_message"),
        requireSendOTP: true,
        actionPrimary: () => {
          axios.request(error.config).then((response) => {
            resolve(response.data);
          });
        },
        actionSecondary: () => {
          resolve();
        },
        sendOTP: User2FAApi.sendOTP,
        resendOTP: User2FAApi.resendOTP,
        verifyOTP: User2FAApi.authenticateOTP,
      });
    });
  }

  if (isResponseInvalid(error.response)) {
    logger.error("Session Invalid");
    const sessionManager = window._SLS.sessionManager;
    if (sessionManager) {
      sessionManager.expireSession();
    }
    return Promise.reject(error);
  }

  // 401 Unauthorized, redirect to log in page if unauthenticated
  if (error.response && error.response.status === 401) {
    // redirect to login
    logger.error("Unauthorized");
    window.location.href = appConfigs.loginUrl;
    return Promise.reject(error);
  }

  // 403 Forbidden, access denied
  if (error.response && error.response.status === 403) {
    logger.error("Forbidden");
    return Promise.reject(error);
  }

  // 404 Not Found, method not exist, entity not found
  if (error.response && error.response.status === 404) {
    logger.error("Not Found");
    return Promise.reject(error);
  }

  // 429 Too Many Requests
  if (error.response && error.response.status === 429) {
    return new Promise((resolve, reject) => {
      axiosRetry(error, httpClient, defaultRetryOptions).then(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  // 500 Internal Server Error
  if (error.response && error.response.status === 500) {
    const errorCode =
      error.response.data.errorMap && error.response.data.errorMap[0]
        ? error.response.data.errorMap[0]
        : null;

    //SLSSIT-6009 show request blocked message
    if (errorCode === ERROR_CODE.REQUEST_BLOCKED) {
      store.dispatch("app/addError", {
        title: i18n.t("api_fetch_request_blocked_error_message"),
      });
      return Promise.reject(error);
    }
    logger.error("%s, ErrorCode:%s", error.response.statusText, errorCode);
    store.dispatch("app/addError", {
      subTitle: errorCode,
      hasTimeout: false,
      id: errorCode,
      code: errorCode,
      statusCode: error.response.status,
    });
    return Promise.reject(error);
  }

  // 501 Not Implemented
  // 502 Bad Gateway
  // 503 Service Unavailable
  // 504 Gateway Timeout
  logger.error(error);
  return Promise.reject(error);
};

const axiosRetryNamespace = "axios-retry";

function getRetryCurrentState(config) {
  const currentState = config[axiosRetryNamespace] || {};
  currentState.retryCount = currentState.retryCount || 0;
  config[axiosRetryNamespace] = currentState;
  return currentState;
}

function getRetryRequestOptions(config, defaultOptions) {
  return { ...defaultOptions, ...config[axiosRetryNamespace] };
}

const defaultRetryOptions = {
  retries: 5,
};

function shouldRetry(retries, currentState) {
  const shouldRetry = currentState.retryCount < retries;

  return shouldRetry;
}

function axiosRetry(error, httpClient, defaultOptions) {
  const { config } = error;
  // If we have no information to retry the request
  if (!config) {
    return Promise.reject(error);
  }

  const { retries = 5 } = getRetryRequestOptions(config, defaultOptions);

  const currentState = getRetryCurrentState(config);

  if (shouldRetry(retries, currentState)) {
    currentState.retryCount += 1;
    const delay = currentState.retryCount * 1000;

    // prevent transform request again when retry
    if (config.transformRequest) {
      config.transformRequest = [];
    }

    return new Promise((resolve) =>
      setTimeout(() => {
        resolve(httpClient(config));
      }, delay),
    );
  }

  return Promise.reject(error);
}

export {
  requestInterceptor,
  responseSuccessInterceptor,
  responseFailInterceptor,
  requestTransformer,
};
